import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ProductNewModel } from '../models/products/product-new-model';

interface UseSharedProductLinkOptions {
    products: ProductNewModel[];
    onProductFound: (product: ProductNewModel) => void;
}

/**
 * Custom hook to handle shared product links
 * Automatically opens the product quick view when a productId is in the URL
 * 
 * @param options - Configuration options
 * @param options.products - Array of available products
 * @param options.onProductFound - Callback when a shared product is found
 */
export const useSharedProductLink = ({
    products,
    onProductFound
}: UseSharedProductLinkOptions): void => {
    const [searchParams, setSearchParams] = useSearchParams();

    useEffect(() => {
        const productId = searchParams.get('productId');

        if (productId && products.length > 0) {
            // Find the product by ID
            const product = products.find(p => p.id === parseInt(productId));

            if (product) {
                // Trigger the callback to show the product
                onProductFound(product);

                // Remove the productId from URL to prevent re-opening on refresh
                const newSearchParams = new URLSearchParams(searchParams);
                newSearchParams.delete('productId');
                setSearchParams(newSearchParams, { replace: true });
            }
        }
    }, [searchParams, products, onProductFound, setSearchParams]);
};
